import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { generateStrongPassword, hashPassword } from '../../utils/security';
import { useLanguage } from '../../hooks/useLanguage';
import { dbUsers } from '../../utils/supabase';
interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ForgotPasswordModal({
  isOpen,
  onClose
}: ForgotPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const {
    language,
    t
  } = useLanguage();
  const isRTL = language === 'ar';
  const maskEmail = (email: string) => {
    const [name, domain] = email.split('@');
    if (name.length <= 2) return email;
    const masked = name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
    return `${masked}@${domain}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      // Check if user exists - try Supabase first, then localStorage
      let user: any = null;
      // 1. Try Supabase first (primary source of truth)
      try {
        const supabaseUser = await dbUsers.getByEmail(email);
        if (supabaseUser) {
          user = {
            name: supabaseUser.name,
            email: supabaseUser.email,
            phone: supabaseUser.phone,
            password: supabaseUser.password,
            instanceName: supabaseUser.instance_name,
            plan: supabaseUser.plan
          };
          console.log('✅ User found in Supabase for password reset');
        }
      } catch (e) {
        console.error('Supabase lookup error:', e);
      }
      // 2. Fallback to localStorage
      if (!user) {
        const userKey = `operiens_user_${email.toLowerCase()}`;
        const userData = localStorage.getItem(userKey);
        const userDataOriginal = localStorage.getItem(`operiens_user_${email}`);
        const stored = userData || userDataOriginal;
        if (stored) {
          user = JSON.parse(stored);
          console.log('✅ User found in localStorage for password reset');
        }
      }
      if (!user) {
        setError(t('error.emailNotRegistered'));
        setIsLoading(false);
        return;
      }
      // Generate new strong password
      const newPassword = generateStrongPassword();
      // Hash the new password
      const hashedPassword = await hashPassword(newPassword);
      // Update Supabase (PRIMARY storage)
      try {
        const updated = await dbUsers.update(email, {
          password: hashedPassword
        } as any);
        if (updated) {
          console.log('✅ Password updated in Supabase');
        } else {
          console.error('❌ Failed to update password in Supabase');
        }
      } catch (e) {
        console.error('Supabase password update error:', e);
      }
      // Also update localStorage as backup
      const updatedUser = {
        ...user,
        password: hashedPassword,
        passwordResetAt: new Date().toISOString()
      };
      localStorage.setItem(`operiens_user_${email.toLowerCase()}`, JSON.stringify(updatedUser));
      localStorage.setItem(`operiens_user_${email}`, JSON.stringify(updatedUser));
      // Also update current user if it's the same email
      const currentUser = localStorage.getItem('operiens_user');
      if (currentUser) {
        const current = JSON.parse(currentUser);
        if (current.email?.toLowerCase() === email.toLowerCase()) {
          localStorage.setItem('operiens_user', JSON.stringify(updatedUser));
        }
      }
      // Send new password (plain text) to webhook for email delivery
      const webhookUrl = 'https://n8n.operines.me/webhook/restpass';
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: user.email || email,
            name: user.name,
            phone: user.phone,
            newPassword: newPassword,
            instanceName: user.instanceName,
            timestamp: new Date().toISOString()
          })
        });
      } catch (fetchError) {
        // Try with no-cors mode
        await fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: user.email || email,
            name: user.name,
            phone: user.phone,
            newPassword: newPassword,
            instanceName: user.instanceName,
            timestamp: new Date().toISOString()
          })
        });
      }
      console.log('Password reset successful. New password generated and saved to Supabase + localStorage.');
      setMaskedEmail(maskEmail(email));
      setIsSent(true);
    } catch (error) {
      console.error('Error:', error);
      setError(t('error.general'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    setEmail('');
    setError('');
    setIsSent(false);
    setMaskedEmail('');
    onClose();
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-49">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-50">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} data-id="element-51" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 overflow-hidden" data-id="element-52">
          <button onClick={handleClose} className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} text-gray-400 hover:text-gray-600 transition-colors`} data-id="element-53">
            <X size={20} data-id="element-54" />
          </button>

          {isSent ? <div className="text-center py-8" data-id="element-55">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-56">
                <CheckCircle className="w-8 h-8 text-green-600" data-id="element-57" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-58">
                {t('forgot.successTitle')}
              </h3>
              <p className="text-gray-500 mb-2" data-id="element-59">{t('forgot.successMessage')}</p>
              <p className="text-[#4f1964] font-medium mb-6" dir="ltr" data-id="element-60">
                {maskedEmail}
              </p>
              <p className="text-sm text-gray-400 mb-6" data-id="element-61">
                {t('forgot.checkInbox')}
              </p>
              <Button onClick={handleClose} fullWidth data-id="element-62">
                {t('forgot.okay')}
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-63">
              <div className="text-center" data-id="element-64">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-65">
                  <Mail className="w-6 h-6 text-blue-600" data-id="element-66" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-67">
                  {t('forgot.title')}
                </h3>
                <p className="text-gray-500 text-sm mt-1" data-id="element-68">
                  {t('forgot.subtitle')}
                </p>
              </div>

              {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-69">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-70" />
                  <p className="text-sm text-red-600" data-id="element-71">{error}</p>
                </motion.div>}

              <Input label={t('auth.email')} type="email" placeholder="name@example.com" value={email} onChange={e => setEmail(e.target.value)} required isRTL={isRTL} data-id="element-72" />

              <Button type="submit" fullWidth isLoading={isLoading} data-id="element-73">
                {t('forgot.sendButton')}
              </Button>
            </form>}
        </motion.div>
      </div>
    </AnimatePresence>;
}